import {
  IonContent,
  IonPage,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonInput,
  IonButton,
  IonSelect,
  IonSelectOption,
  IonIcon,
  IonSpinner,
  IonText,
} from '@ionic/react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWallet } from '../contexts/WalletContext';
import {
  gift,
  mail,
  checkmarkCircle,
  closeCircle,
  informationCircle,
} from 'ionicons/icons';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import {
  getAvailableBrands,
  validateRedemption,
  redeemGiftCard,
  coinsToDollars,
  dollarsToCoins,
  GIFT_CARD_BRANDS,
  type GiftCardBrand,
} from '../services/GiftCardService';
import './GiftCardRedemption.css';

const GiftCardRedemption: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { balance, redeemCoinsForGiftCard } = useWallet();
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [amount, setAmount] = useState<number>(5);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [giftCardCode, setGiftCardCode] = useState<string | null>(null);
  const [giftCardLink, setGiftCardLink] = useState<string | null>(null);

  const availableBrands = getAvailableBrands();
  const maxDollars = coinsToDollars(balance);
  const coinsNeeded = dollarsToCoins(amount);

  // Pre-fill email with user's email if available
  useEffect(() => {
    if (currentUser?.email && !recipientEmail) {
      setRecipientEmail(currentUser.email);
    }
  }, [currentUser, recipientEmail]);

  // Update amount when brand changes to respect brand limits
  useEffect(() => {
    if (selectedBrand) {
      const brand = GIFT_CARD_BRANDS.find(b => b.id === selectedBrand);
      if (brand) {
        const currentAmount = amount;
        if (currentAmount < brand.minAmount) {
          setAmount(brand.minAmount);
        } else if (currentAmount > brand.maxAmount) {
          setAmount(Math.min(brand.maxAmount, maxDollars));
        }
      }
    }
  }, [selectedBrand, maxDollars]);

  // Validate amount against max available
  useEffect(() => {
    if (amount > maxDollars) {
      setAmount(maxDollars);
    }
  }, [maxDollars, amount]);

  const handleAmountChange = (value: string) => {
    const numValue = parseFloat(value) || 0;
    if (selectedBrand) {
      const brand = GIFT_CARD_BRANDS.find(b => b.id === selectedBrand);
      if (brand) {
        if (numValue < brand.minAmount) {
          setAmount(brand.minAmount);
        } else if (numValue > brand.maxAmount) {
          setAmount(Math.min(brand.maxAmount, maxDollars));
        } else {
          setAmount(Math.min(numValue, maxDollars));
        }
      } else {
        setAmount(Math.min(numValue, maxDollars));
      }
    } else {
      setAmount(Math.min(numValue, maxDollars));
    }
  };

  const handleRedeem = async () => {
    if (!currentUser) {
      setError('You must be logged in to redeem gift cards');
      return;
    }

    if (!selectedBrand) {
      setError('Please select a gift card brand');
      return;
    }

    if (!recipientEmail || !recipientEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate redemption
    const validation = validateRedemption(selectedBrand, amount, balance);
    if (!validation.valid) {
      setError(validation.error || 'Invalid redemption request');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Redeem gift card through API
      const redemption = await redeemGiftCard(
        currentUser.uid,
        selectedBrand,
        amount,
        recipientEmail
      );

      if (redemption.status === 'failed') {
        setError(redemption.errorMessage || 'Failed to redeem gift card. Please try again.');
        setLoading(false);
        return;
      }

      // Deduct coins from wallet
      const brand = GIFT_CARD_BRANDS.find(b => b.id === selectedBrand);
      const description = `Redeemed ${brand?.name || 'Gift Card'} $${amount} gift card`;
      const success = await redeemCoinsForGiftCard(coinsNeeded, description, {
        brandId: selectedBrand,
        brandName: brand?.name,
        amount,
        recipientEmail,
        redemptionId: redemption.giftCardCode,
      });

      if (!success) {
        setError('Failed to process redemption. Please try again.');
        setLoading(false);
        return;
      }

      // Success!
      setSuccess(true);
      setGiftCardCode(redemption.giftCardCode || null);
      setGiftCardLink(redemption.giftCardLink || null);
    } catch (error) {
      console.error('Gift card redemption error:', error);
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleNewRedemption = () => {
    setSuccess(false);
    setError(null);
    setSelectedBrand('');
    setAmount(5);
    setGiftCardCode(null);
    setGiftCardLink(null);
  };

  const selectedBrandData = GIFT_CARD_BRANDS.find(b => b.id === selectedBrand);

  return (
    <IonPage>
      <AppHeader title="Redeem Gift Card" showBackButton={true} defaultHref="/wallet" />
      <IonContent fullscreen className="ion-padding">
        <div className="gift-card-container">
          {success ? (
            <IonCard className="success-card">
              <IonCardContent>
                <div className="success-content">
                  <IonIcon icon={checkmarkCircle} className="success-icon" />
                  <h2>Gift Card Redeemed Successfully!</h2>
                  <p className="success-message">
                    Your {selectedBrandData?.name} gift card for ${amount} has been sent to:
                  </p>
                  <p className="success-email">{recipientEmail}</p>
                  
                  {giftCardCode && (
                    <div className="gift-card-code">
                      <p className="code-label">Gift Card Code:</p>
                      <p className="code-value">{giftCardCode}</p>
                    </div>
                  )}

                  {giftCardLink && (
                    <IonButton
                      expand="block"
                      color="primary"
                      href={giftCardLink}
                      target="_blank"
                      className="gift-card-link-button"
                    >
                      View Gift Card
                    </IonButton>
                  )}

                  <div className="success-actions">
                    <IonButton
                      expand="block"
                      fill="outline"
                      onClick={handleNewRedemption}
                    >
                      Redeem Another Gift Card
                    </IonButton>
                    <IonButton
                      expand="block"
                      fill="clear"
                      onClick={() => navigate('/wallet')}
                    >
                      Back to Wallet
                    </IonButton>
                  </div>
                </div>
              </IonCardContent>
            </IonCard>
          ) : (
            <>
              {/* Balance Info */}
              <IonCard className="balance-info-card">
                <IonCardContent>
                  <div className="balance-info">
                    <div className="balance-item">
                      <span className="balance-label">Available Coins:</span>
                      <span className="balance-value">{balance.toLocaleString()}</span>
                    </div>
                    <div className="balance-item">
                      <span className="balance-label">Max Redemption:</span>
                      <span className="balance-value">${maxDollars}</span>
                    </div>
                  </div>
                </IonCardContent>
              </IonCard>

              {/* Redemption Form */}
              <IonCard className="redemption-form-card">
                <IonCardHeader>
                  <IonCardTitle>Select Gift Card</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <div className="form-group">
                    <IonItem className="modern-input-item" lines="none">
                      <IonLabel position="stacked" className="input-label">
                        Gift Card Brand
                      </IonLabel>
                      <IonSelect
                        value={selectedBrand}
                        placeholder="Choose a brand"
                        onIonChange={(e) => setSelectedBrand(e.detail.value)}
                        className="modern-select"
                      >
                        {availableBrands.map((brand) => (
                          <IonSelectOption key={brand.id} value={brand.id}>
                            {brand.name}
                          </IonSelectOption>
                        ))}
                      </IonSelect>
                    </IonItem>

                    {selectedBrand && selectedBrandData && (
                      <div className="brand-info">
                        <IonText color="medium">
                          <p className="brand-limits">
                            Available: ${selectedBrandData.minAmount} - ${selectedBrandData.maxAmount}
                          </p>
                        </IonText>
                      </div>
                    )}

                    <IonItem className="modern-input-item" lines="none">
                      <IonLabel position="stacked" className="input-label">
                        Amount ($)
                      </IonLabel>
                      <IonInput
                        type="number"
                        value={amount.toString()}
                        placeholder="Enter amount"
                        min={selectedBrandData?.minAmount || 5}
                        max={Math.min(selectedBrandData?.maxAmount || 100, maxDollars)}
                        onIonInput={(e) => handleAmountChange(e.detail.value as string)}
                        className="modern-input"
                      />
                    </IonItem>

                    <div className="amount-info">
                      <IonText color="medium">
                        <p className="coins-needed">
                          {coinsNeeded.toLocaleString()} coins needed
                        </p>
                      </IonText>
                    </div>

                    <IonItem className="modern-input-item" lines="none">
                      <IonIcon icon={mail} slot="start" className="input-icon" />
                      <IonLabel position="stacked" className="input-label">
                        Recipient Email
                      </IonLabel>
                      <IonInput
                        type="email"
                        value={recipientEmail}
                        placeholder="your.email@example.com"
                        onIonInput={(e) => setRecipientEmail(e.detail.value as string)}
                        className="modern-input"
                      />
                    </IonItem>

                    {error && (
                      <div className="error-message">
                        <IonIcon icon={closeCircle} className="error-icon" />
                        <IonText color="danger">{error}</IonText>
                      </div>
                    )}

                    <IonButton
                      expand="block"
                      color="primary"
                      onClick={handleRedeem}
                      disabled={loading || !selectedBrand || !recipientEmail || amount <= 0 || coinsNeeded > balance}
                      className="redeem-button"
                    >
                      {loading ? (
                        <>
                          <IonSpinner name="crescent" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <IonIcon icon={gift} slot="start" />
                          Redeem Gift Card
                        </>
                      )}
                    </IonButton>
                  </div>
                </IonCardContent>
              </IonCard>

              {/* Info Card */}
              <IonCard className="info-card-modern">
                <IonCardContent>
                  <div className="info-note">
                    <IonIcon icon={informationCircle} className="info-note-icon" />
                    <div className="info-note-content">
                      <p className="info-note-title">How It Works</p>
                      <ul className="info-note-list">
                        <li>Select your preferred gift card brand</li>
                        <li>Choose the amount (minimum $5)</li>
                        <li>Enter the email where you want to receive the gift card</li>
                        <li>Your coins will be deducted and the gift card will be sent via email</li>
                        <li>Gift cards are typically delivered within minutes</li>
                      </ul>
                    </div>
                  </div>
                </IonCardContent>
              </IonCard>
            </>
          )}
        </div>
      </IonContent>
      <BottomNav />
    </IonPage>
  );
};

export default GiftCardRedemption;

